import React, { useState } from 'react';
import { AlertTriangle, Sparkles, Loader2, Wrench, ListChecks, Package, Clock, Info } from 'lucide-react';
import { carsysApi } from '../api/carsysApi';
import { useT } from '../context/LanguageContext';
import { toast } from 'sonner';

const lab = { fontSize: 12, fontWeight: 600, color: 'var(--cs-text-muted)', display: 'block', margin: '0 0 4px' };
const inp = { width: '100%', padding: '9px 11px', border: '1px solid var(--cs-border)', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };

export default function FoutcodeUitleg() {
  const { t } = useT();
  const [d, setD] = useState({ code: '', merk: '', model: '', bouwjaar: '', motor: '', klacht: '' });
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const upd = (k, v) => setD(p => ({ ...p, [k]: v }));

  const verklaar = async () => {
    if (!d.code.trim()) { toast.error(t('Vul minimaal een foutcode in (bv. P0300)', 'Vul minimaal een foutcode in (bv. P0300)')); return; }
    setBusy(true); setRes(null);
    try { setRes(await carsysApi.aiFoutcode(d)); }
    catch (e) { toast.error(t('Mislukt', 'Mislukt'), { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="cs-page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: '#dc2626', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><AlertTriangle size={20} /></div>
          <div><h2>{t('AI Foutcode-uitleg', 'AI Foutcode-uitleg')}</h2><p>{t('Begrijp OBD-/DTC-codes: oorzaken, onderdelen en arbeidstijd', 'Begrijp OBD-/DTC-codes: oorzaken, onderdelen en arbeidstijd')}</p></div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 380px) 1fr', gap: 18, alignItems: 'start' }}>
        <div className="cs-card padded">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div style={{ gridColumn: '1 / 3' }}><label style={lab}>{t('Foutcode(s)', 'Foutcode(s)')} *</label><input style={inp} placeholder="P0300, P0420" value={d.code} onChange={e => upd('code', e.target.value)} data-testid="fc-code" /></div>
            <div><label style={lab}>{t('Merk', 'Merk')}</label><input style={inp} value={d.merk} onChange={e => upd('merk', e.target.value)} data-testid="fc-merk" /></div>
            <div><label style={lab}>{t('Model', 'Model')}</label><input style={inp} value={d.model} onChange={e => upd('model', e.target.value)} data-testid="fc-model" /></div>
            <div><label style={lab}>{t('Bouwjaar', 'Bouwjaar')}</label><input style={inp} value={d.bouwjaar} onChange={e => upd('bouwjaar', e.target.value)} data-testid="fc-bouwjaar" /></div>
            <div><label style={lab}>{t('Motor / inhoud', 'Motor / inhoud')}</label><input style={inp} value={d.motor} onChange={e => upd('motor', e.target.value)} data-testid="fc-motor" /></div>
            <div style={{ gridColumn: '1 / 3' }}><label style={lab}>{t('Klacht (optioneel)', 'Klacht (optioneel)')}</label><textarea style={{ ...inp, minHeight: 64, resize: 'vertical' }} value={d.klacht} onChange={e => upd('klacht', e.target.value)} data-testid="fc-klacht" /></div>
          </div>
          <button className="cs-btn primary" style={{ width: '100%', marginTop: 14, background: '#dc2626', borderColor: '#dc2626' }} onClick={verklaar} disabled={busy} data-testid="fc-verklaar">
            {busy ? <Loader2 size={16} className="spin" /> : <Sparkles size={16} />} {t('Verklaar foutcode', 'Verklaar foutcode')}
          </button>
        </div>

        <div>
          {!res && !busy && (
            <div className="cs-card padded" style={{ textAlign: 'center', color: 'var(--cs-text-muted)', padding: 48 }}>
              <AlertTriangle size={40} style={{ opacity: 0.3 }} />
              <p style={{ marginTop: 12 }}>{t('Vul een foutcode in en klik op "Verklaar foutcode" voor een AI-uitleg.', 'Vul een foutcode in en klik op "Verklaar foutcode" voor een AI-uitleg.')}</p>
            </div>
          )}
          {busy && <div className="cs-card padded" style={{ textAlign: 'center', padding: 48 }}><Loader2 size={32} className="spin" style={{ color: '#dc2626' }} /><p style={{ marginTop: 10, color: 'var(--cs-text-muted)' }}>{t('AI analyseert…', 'AI analyseert…')}</p></div>}
          {res && (
            <div data-testid="fc-result" style={{ display: 'grid', gap: 14 }}>
              {Array.isArray(res.codes) && res.codes.length > 0 && (
                <div className="cs-card padded">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7, color: '#dc2626', fontWeight: 700, fontSize: 12.5, marginBottom: 10 }}><AlertTriangle size={16} /> {t('Betekenis', 'Betekenis')}</div>
                  {res.codes.map((c, i) => (
                    <div key={i} style={{ padding: '8px 0', borderTop: i ? '1px solid var(--cs-border)' : 'none' }}>
                      <div style={{ fontWeight: 800, fontSize: 15 }}>{c.code} {c.systeem && <span style={{ fontWeight: 500, fontSize: 12, color: 'var(--cs-text-muted)' }}>· {c.systeem}</span>}</div>
                      <div style={{ color: 'var(--cs-text)', marginTop: 2 }}>{c.betekenis}</div>
                    </div>
                  ))}
                </div>
              )}
              <Lijst icon={<Wrench size={16} />} kleur="#d97706" titel={t('Waarschijnlijke oorzaken', 'Waarschijnlijke oorzaken')} items={res.waarschijnlijkeOorzaken} genummerd testid="fc-oorzaken" />
              <Lijst icon={<ListChecks size={16} />} kleur="#2563eb" titel={t('Diagnosestappen', 'Diagnosestappen')} items={res.diagnoseStappen} genummerd />
              <Lijst icon={<Package size={16} />} kleur="#16a34a" titel={t('Benodigde onderdelen', 'Benodigde onderdelen')} items={res.benodigdeOnderdelen} />
              {res.geschatteArbeidstijdUur != null && (
                <div className="cs-card padded" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Clock size={18} style={{ color: '#7c3aed' }} />
                  <span style={{ fontWeight: 600 }}>{t('Geschatte arbeidstijd', 'Geschatte arbeidstijd')}:</span>
                  <span style={{ fontSize: 18, fontWeight: 800, color: '#7c3aed' }}>{res.geschatteArbeidstijdUur} {t('uur', 'uur')}</span>
                </div>
              )}
              {res.let_op && <div style={{ fontSize: 12, color: '#b45309', display: 'flex', gap: 6, alignItems: 'center' }}><Info size={14} /> {res.let_op}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Lijst = ({ icon, kleur, titel, items, genummerd, testid }) => {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div className="cs-card padded" data-testid={testid}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, color: kleur, fontWeight: 700, fontSize: 12.5, marginBottom: 8 }}>{icon} {titel}</div>
      <ol style={{ margin: 0, paddingLeft: genummerd ? 20 : 18, listStyle: genummerd ? 'decimal' : 'disc', lineHeight: 1.7 }}>
        {items.map((it, i) => <li key={i} style={{ color: 'var(--cs-text)' }}>{it}</li>)}
      </ol>
    </div>
  );
};
